<template>
    <div class="calendar-page">
        <!-- Filters and Controls -->
        <div class="calendar-toolbar mb-3">
            <div class="row align-items-center">
                <div class="col-auto">
                    <div class="btn-group">
                        <button 
                            class="btn btn-sm"
                            :class="viewMode === 'month' ? 'btn-outline-primary active' : 'btn-outline-primary'"
                            @click="viewMode = 'month'"
                        >
                            <i class="fas fa-calendar-alt me-1"></i> Monthly
                        </button>
                        <button 
                            class="btn btn-sm"
                            :class="viewMode === 'week' ? 'btn-outline-primary active' : 'btn-outline-primary'"
                            @click="viewMode = 'week'"
                        >
                            <i class="fas fa-calendar-week me-1"></i> Weekly
                        </button>
                        <button 
                            class="btn btn-sm"
                            :class="viewMode === 'day' ? 'btn-outline-primary active' : 'btn-outline-primary'"
                            @click="viewMode = 'day'"
                        >
                            <i class="fas fa-calendar-day me-1"></i> Daily
                        </button>
                    </div>
                </div>
                
                <div class="col-auto">
                    <div class="btn-group">
                        <button class="btn btn-sm btn-outline-secondary" @click="previousPeriod">
                            <i class="fas fa-chevron-left"></i>
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" @click="goToToday">
                            Today
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" @click="nextPeriod">
                            <i class="fas fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
                
                <div class="col">
                    <h4 class="mb-0">{{ currentPeriodLabel }}</h4>
                </div>
                
                <div class="col-auto">
                    <kt-autoselect
                        label="Task Type"
                        :options="timeTypeOptions"
                        display-by="name"
                        value-by="id"
                        v-model="filters.timeType"
                        @change="loadTasks"
                        :badge-count="(filters.timeType && filters.timeType !== 'All') ? 1 : 0"
                    ></kt-autoselect>
                </div>
                
                <div class="col-auto">
                    <tech-multi-check 
                        label="Technicians"
                        :model-value="filters.technician"
                        @update:model-value="(value: any) => { filters.technician = value; loadTasks(); }"
                    ></tech-multi-check>
                </div>
                
                <div class="col-auto">
                    <div class="btn-group">
                        <button 
                            class="btn btn-sm btn-outline-primary"
                            @click="openTaskModal(null)"
                        >
                            <i class="fas fa-plus me-1"></i>
                            New Task
                        </button>
                        <button 
                            v-if="selectedTask"
                            class="btn btn-sm btn-primary"
                            @click="openTaskModal(selectedTask)"
                        >
                            <i class="fas fa-edit me-1"></i>
                            Edit Task
                        </button>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Calendar Views -->
        <div class="calendar-container" @click="deselectTask">
            <!-- Month View -->
            <div v-if="viewMode === 'month'" class="calendar-month">
                <div class="calendar-header">
                    <div class="calendar-day-header" v-for="day in weekDays" :key="day">
                        {{ day }}
                    </div>
                </div>
                <div class="calendar-grid">
                    <div 
                        v-for="(day, index) in monthDays" 
                        :key="index"
                        class="calendar-day"
                        :class="{ 
                            'other-month': !day.isCurrentMonth,
                            'today': day.isToday,
                            'weekend': day.isWeekend
                        }"
                        @dragover.prevent
                        @drop="handleDrop($event, day.date)"
                    >
                        <div class="day-number">{{ day.dayNumber }}</div>
                        <div class="day-tasks">
                            <div 
                                v-for="task in getTasksForDay(day.date)" 
                                :key="String(task.Id || Math.random())"
                                class="task-item"
                                :class="getTaskClass(task)"
                                draggable="true"
                                @dragstart="handleDragStart($event, task)"
                                @click.stop="selectTask(task)"
                            >
                                <div class="task-time" v-if="task.CompleteByDate || task.WorkCompleteDate">
                                    {{ formatTime(task.CompleteByDate || task.WorkCompleteDate) }}
                                </div>
                                <div class="task-type-label">{{ getTaskTypeLabel(task) }}</div>
                                <div class="task-title">{{ getTaskTitle(task) }}</div>
                                <div class="task-tech" v-if="task.Technician">
                                    <i class="fas fa-user me-1"></i>{{ task.Technician.TechnicianName }}
                                </div>
                                <div class="task-client" v-if="task.Request?.Contact?.Company">
                                    <i class="fas fa-building me-1"></i>{{ task.Request.Contact.Company }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Week View -->
            <div v-if="viewMode === 'week'" class="calendar-week">
                <div class="week-grid">
                    <!-- Sticky Header -->
                    <div class="week-header">
                        <div class="time-column-header">Time</div>
                        <div 
                            v-for="day in weekDays" 
                            :key="day" 
                            class="week-day-header"
                            :class="{ 'today': isToday(weekDates[weekDays.indexOf(day)]) }"
                        >
                            <div class="day-name">{{ day }}</div>
                            <div class="day-date">{{ formatDate(weekDates[weekDays.indexOf(day)]) }}</div>
                        </div>
                    </div>
                    
                    <!-- Time Column -->
                    <div class="time-column">
                        <div v-for="hour in 24" :key="hour" class="time-slot">
                            {{ formatHour(hour - 1) }}
                        </div>
                    </div>
                    <div 
                        v-for="(date, dayIndex) in weekDates" 
                        :key="dayIndex"
                        class="week-day-column"
                        :class="{ 'today': isToday(date) }"
                    >
                        <div 
                            v-for="hour in 24" 
                            :key="hour"
                            class="week-time-slot"
                            @dragover.prevent
                            @drop="handleDropTime($event, date, hour - 1)"
                        >
                            <div 
                                v-for="task in getTasksForDayHour(date, hour - 1)" 
                                :key="String(task.Id || Math.random())"
                                class="task-item-week"
                                :class="getTaskClass(task)"
                                draggable="true"
                                @dragstart="handleDragStart($event, task)"
                                @click.stop="selectTask(task)"
                            >
                                <div class="task-type-label-week">{{ getTaskTypeLabel(task) }}</div>
                                <div class="task-title-week">{{ getTaskTitle(task) }}</div>
                                <div class="task-tech-week" v-if="task.Technician">
                                    <i class="fas fa-user me-1"></i>{{ task.Technician.TechnicianName }}
                                </div>
                                <div class="task-client-week" v-if="task.Request?.Contact?.Company">
                                    <i class="fas fa-building me-1"></i>{{ task.Request.Contact.Company }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Day View -->
            <div v-if="viewMode === 'day'" class="calendar-day-view">
                <div class="day-view-header">
                    <h5>{{ formatDayHeader(currentDate) }}</h5>
                </div>
                <div class="day-view-grid">
                    <div class="time-column">
                        <div v-for="hour in 24" :key="hour" class="time-slot">
                            {{ formatHour(hour - 1) }}
                        </div>
                    </div>
                    <div class="day-view-column">
                        <div 
                            v-for="hour in 24" 
                            :key="hour"
                            class="day-time-slot"
                            @dragover.prevent
                            @drop="handleDropTime($event, currentDate, hour - 1)"
                        >
                            <div 
                                v-for="task in getTasksForDayHour(currentDate, hour - 1)" 
                                :key="String(task.Id || Math.random())"
                                class="task-item-day"
                                :class="getTaskClass(task)"
                                draggable="true"
                                @dragstart="handleDragStart($event, task)"
                                @click.stop="selectTask(task)"
                            >
                                <div class="task-time-day">{{ formatTime(task.CompleteByDate || task.WorkCompleteDate) }}</div>
                                <div class="task-title-day">{{ getTaskTitle(task) }}</div>
                                <div class="task-tech" v-if="task.Technician">{{ task.Technician.TechnicianName }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Task Edit Modal -->
        <work-edit-embedded 
            v-if="showTaskModal && editingTaskId && editingTaskType === 'work'" 
            :record-id="editingTaskId"
            @close="closeTaskModal"
        />
        <assignment-edit-embedded 
            v-if="showTaskModal && editingTaskId && editingTaskType === 'assignment'" 
            :record-id="editingTaskId"
            @close="closeTaskModal"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import Api from '@/services/ApiService';
import { TimeModel } from '@/models/TaskModel';
import KtAutoselect from '@/components/UI/KtAutoselect.vue';
import TechMultiCheck from '@/components/Techs/TechMultiCheck.vue';
import WorkEditEmbedded from '@/components/Work/WorkEditEmbedded.vue';
import AssignmentEditEmbedded from '@/components/Assignments/AssignmentEditEmbedded.vue';
import dayjs from 'dayjs';

// Data
const route = useRoute();
const viewMode = ref<'month' | 'week' | 'day'>('month');
const currentDate = ref(new Date());
const tasks = ref<TimeModel[]>([]);
const selectedTask = ref<TimeModel | null>(null);
const draggedTask = ref<TimeModel | null>(null);
const showTaskModal = ref(false);
const editingTaskId = ref<string | null>(null);
const editingTaskType = ref<'work' | 'assignment' | null>(null);

const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const timeTypeOptions = ref([
    { id: 'All', name: 'All' },
    { id: 'Assignments', name: 'Assignments' },
    { id: 'Work', name: 'Work' }
]);

const filters = ref({
    timeType: 'All',
    technician: [] as number[]
});

// Computed
const currentPeriodLabel = computed(() => {
    if (viewMode.value === 'month') {
        return dayjs(currentDate.value).format('MMMM YYYY');
    } else if (viewMode.value === 'week') {
        const start = weekDates.value[0];
        const end = weekDates.value[6];
        return `${dayjs(start).format('MMM D')} - ${dayjs(end).format('MMM D, YYYY')}`;
    } else {
        return dayjs(currentDate.value).format('dddd, MMMM D, YYYY');
    }
});

const monthDays = computed(() => {
    const year = currentDate.value.getUTCFullYear();
    const month = currentDate.value.getUTCMonth();
    
    // Create dates in UTC to match task dates
    const firstDay = new Date(Date.UTC(year, month, 1));
    const lastDay = new Date(Date.UTC(year, month + 1, 0));
    const prevLastDay = new Date(Date.UTC(year, month, 0));
    
    const firstDayOfWeek = firstDay.getUTCDay();
    const daysInMonth = lastDay.getUTCDate();
    const daysInPrevMonth = prevLastDay.getUTCDate();
    
    const days = [];
    
    // Previous month days
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        const day = daysInPrevMonth - i;
        const date = new Date(Date.UTC(year, month - 1, day));
        days.push({
            dayNumber: day,
            date: date,
            isCurrentMonth: false,
            isToday: false,
            isWeekend: date.getUTCDay() === 0 || date.getUTCDay() === 6
        });
    }
    
    // Current month days
    const today = new Date();
    for (let i = 1; i <= daysInMonth; i++) {
        const date = new Date(Date.UTC(year, month, i));
        const isTodayUTC = date.getUTCFullYear() === today.getUTCFullYear() &&
                           date.getUTCMonth() === today.getUTCMonth() &&
                           date.getUTCDate() === today.getUTCDate();
        days.push({
            dayNumber: i,
            date: date,
            isCurrentMonth: true,
            isToday: isTodayUTC,
            isWeekend: date.getUTCDay() === 0 || date.getUTCDay() === 6
        });
    }
    
    // Next month days
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let i = 1; i <= remainingDays; i++) {
        const date = new Date(Date.UTC(year, month + 1, i));
        days.push({
            dayNumber: i,
            date: date,
            isCurrentMonth: false,
            isToday: false,
            isWeekend: date.getUTCDay() === 0 || date.getUTCDay() === 6
        });
    }
    
    return days;
});

const weekDates = computed(() => {
    const dates = [];
    // Create start of week in UTC
    const year = currentDate.value.getUTCFullYear();
    const month = currentDate.value.getUTCMonth();
    const day = currentDate.value.getUTCDate();
    const startOfWeek = new Date(Date.UTC(year, month, day));
    const dayOfWeek = startOfWeek.getUTCDay();
    startOfWeek.setUTCDate(startOfWeek.getUTCDate() - dayOfWeek);
    
    for (let i = 0; i < 7; i++) {
        const date = new Date(Date.UTC(
            startOfWeek.getUTCFullYear(),
            startOfWeek.getUTCMonth(),
            startOfWeek.getUTCDate() + i
        ));
        dates.push(date);
    }
    
    return dates;
});

// Methods
const loadTasks = async () => {
    try {
        let startDate: Date;
        let endDate: Date;
        
        if (viewMode.value === 'month') {
            const year = currentDate.value.getFullYear();
            const month = currentDate.value.getMonth();
            startDate = new Date(year, month, 1);
            startDate.setDate(startDate.getDate() - startDate.getDay()); // Start from Sunday before month
            endDate = new Date(year, month + 1, 0);
            endDate.setDate(endDate.getDate() + (6 - endDate.getDay())); // End on Saturday after month
        } else if (viewMode.value === 'week') {
            startDate = weekDates.value[0];
            endDate = weekDates.value[6];
        } else {
            startDate = new Date(currentDate.value);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(currentDate.value);
            endDate.setHours(23, 59, 59, 999);
        }
        
        const params: any = {
            StartDate: startDate,
            EndDate: endDate,
            IncludeRequest: true,
            IncludeContact: true,
            IncludeTechnician: true,
            Size: 1000
        };
        
        if (filters.value.timeType !== 'All') {
            params.TimeType = filters.value.timeType;
        }
        
        if (filters.value.technician.length > 0) {
            params.TechId = filters.value.technician;
        }
        
        const response = await Api.loadTimes(params, false);
        tasks.value = response.Data || [];
    } catch (error) {
        console.error('Failed to load tasks:', error);
        tasks.value = [];
    }
};

const getTasksForDay = (date: Date) => {
    return tasks.value.filter(task => {
        const taskDate = task.CompleteByDate || task.WorkCompleteDate;
        if (!taskDate) return false;
        const taskDay = new Date(taskDate);
        // Compare UTC dates only (ignore time and timezone)
        return taskDay.getUTCFullYear() === date.getUTCFullYear() &&
               taskDay.getUTCMonth() === date.getUTCMonth() &&
               taskDay.getUTCDate() === date.getUTCDate();
    }).sort((a, b) => {
        const dateA = new Date(a.CompleteByDate || a.WorkCompleteDate || 0);
        const dateB = new Date(b.CompleteByDate || b.WorkCompleteDate || 0);
        return dateA.getTime() - dateB.getTime();
    });
};

const getTasksForDayHour = (date: Date, hour: number) => {
    return tasks.value.filter(task => {
        const taskDate = task.CompleteByDate || task.WorkCompleteDate;
        if (!taskDate) return false;
        const taskDay = new Date(taskDate);
        // Compare UTC date and hour
        return taskDay.getUTCFullYear() === date.getUTCFullYear() &&
               taskDay.getUTCMonth() === date.getUTCMonth() &&
               taskDay.getUTCDate() === date.getUTCDate() &&
               taskDay.getUTCHours() === hour;
    }).sort((a, b) => {
        const dateA = new Date(a.CompleteByDate || a.WorkCompleteDate || 0);
        const dateB = new Date(b.CompleteByDate || b.WorkCompleteDate || 0);
        return dateA.getTime() - dateB.getTime();
    });
};

const getTaskTitle = (task: TimeModel) => {
    if (task.WorkAssignment) return task.WorkAssignment;
    if (task.ActualWork) return task.ActualWork;
    if (task.InvoiceText) return task.InvoiceText;
    return `Task #${task.Id}`;
};

const getTaskClass = (task: TimeModel) => {
    const classes = [];
    
    // Assignment vs Work
    if (task.WorkAssignment && !task.ActualWork) {
        classes.push('task-assignment');
    } else {
        classes.push('task-work');
    }
    
    // Status-based coloring
    if (task.Status) {
        classes.push(`task-status-${task.Status}`);
    }
    
    if (selectedTask.value?.Id === task.Id) {
        classes.push('task-selected');
    }
    
    return classes.join(' ');
};

const formatTime = (dateStr: string | Date | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return dayjs(date).format('h:mm A');
};

const formatDate = (date: Date) => {
    return dayjs(date).format('M/D');
};

const formatHour = (hour: number) => {
    return dayjs().hour(hour).minute(0).format('h A');
};

const formatDayHeader = (date: Date) => {
    return dayjs(date).format('dddd, MMMM D, YYYY');
};

const getTaskTypeLabel = (task: TimeModel) => {
    // Determine if it's Work or Task/Assignment
    return (task.WorkAssignment && !task.ActualWork) ? 'Task' : 'Work';
};

const isToday = (date: Date) => {
    const today = new Date();
    // Compare UTC dates to avoid timezone issues
    return date.getUTCFullYear() === today.getUTCFullYear() &&
           date.getUTCMonth() === today.getUTCMonth() &&
           date.getUTCDate() === today.getUTCDate();
};

const previousPeriod = () => {
    if (viewMode.value === 'month') {
        const year = currentDate.value.getUTCFullYear();
        const month = currentDate.value.getUTCMonth();
        currentDate.value = new Date(Date.UTC(year, month - 1, 1));
    } else if (viewMode.value === 'week') {
        // Use UTC-based date manipulation to avoid timezone issues
        const year = currentDate.value.getUTCFullYear();
        const month = currentDate.value.getUTCMonth();
        const day = currentDate.value.getUTCDate();
        currentDate.value = new Date(Date.UTC(year, month, day - 7));
    } else {
        const year = currentDate.value.getUTCFullYear();
        const month = currentDate.value.getUTCMonth();
        const day = currentDate.value.getUTCDate();
        currentDate.value = new Date(Date.UTC(year, month, day - 1));
    }
    loadTasks();
};

const nextPeriod = () => {
    if (viewMode.value === 'month') {
        const year = currentDate.value.getUTCFullYear();
        const month = currentDate.value.getUTCMonth();
        currentDate.value = new Date(Date.UTC(year, month + 1, 1));
    } else if (viewMode.value === 'week') {
        // Use UTC-based date manipulation to avoid timezone issues
        const year = currentDate.value.getUTCFullYear();
        const month = currentDate.value.getUTCMonth();
        const day = currentDate.value.getUTCDate();
        currentDate.value = new Date(Date.UTC(year, month, day + 7));
    } else {
        const year = currentDate.value.getUTCFullYear();
        const month = currentDate.value.getUTCMonth();
        const day = currentDate.value.getUTCDate();
        currentDate.value = new Date(Date.UTC(year, month, day + 1));
    }
    loadTasks();
};

const goToToday = () => {
    currentDate.value = new Date();
    loadTasks();
};

const selectTask = (task: TimeModel) => {
    // Toggle: deselect if clicking the same task
    if (selectedTask.value?.Id === task.Id) {
        selectedTask.value = null;
    } else {
        selectedTask.value = task;
    }
};

const deselectTask = () => {
    selectedTask.value = null;
};

const openTaskModal = (task: TimeModel | null) => {
    const taskToEdit = task || selectedTask.value;
    if (taskToEdit) {
        editingTaskId.value = taskToEdit.Id?.toString() || null;
        // Determine task type: Assignment has WorkAssignment but no ActualWork
        editingTaskType.value = (taskToEdit.WorkAssignment && !taskToEdit.ActualWork) ? 'assignment' : 'work';
    } else {
        editingTaskId.value = 'new';
        editingTaskType.value = 'assignment'; // Default to assignment for new tasks
    }
    showTaskModal.value = true;
};

const closeTaskModal = () => {
    showTaskModal.value = false;
    editingTaskId.value = null;
    editingTaskType.value = null;
    selectedTask.value = null;
    loadTasks();
};

const handleDragStart = (event: DragEvent, task: TimeModel) => {
    draggedTask.value = task;
    event.dataTransfer!.effectAllowed = 'move';
};

const handleDrop = async (event: DragEvent, date: Date) => {
    event.preventDefault();
    console.log('handleDrop called with date:', date, 'draggedTask:', draggedTask.value);
    
    if (!draggedTask.value) {
        console.log('No dragged task, returning');
        return;
    }
    
    try {
        const task = draggedTask.value;
        
        // Validate date
        if (!date || isNaN(date.getTime())) {
            console.error('Invalid date passed to handleDrop:', date, 'type:', typeof date);
            return;
        }
        
        // Create date in UTC to avoid timezone shifts
        console.log('Target date:', date, 'UTC components:', date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
        const newDate = new Date(Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            12, 0, 0, 0 // Use noon UTC to avoid day-boundary issues
        ));
        console.log('Created newDate (noon UTC):', newDate, newDate.toISOString());
        
        // Preserve time if it exists
        if (task.CompleteByDate || task.WorkCompleteDate) {
            const oldDate = new Date(task.CompleteByDate || task.WorkCompleteDate || '');
            console.log('Old date:', task.CompleteByDate || task.WorkCompleteDate, 'parsed as:', oldDate);
            if (!isNaN(oldDate.getTime())) {
                console.log('Preserving time - old hours:', oldDate.getHours(), 'minutes:', oldDate.getMinutes());
                newDate.setHours(oldDate.getHours(), oldDate.getMinutes(), 0, 0);
                console.log('After preserving time:', newDate, newDate.toISOString());
            }
        }
        
        // Validate newDate before saving
        if (isNaN(newDate.getTime())) {
            console.error('Invalid newDate created:', newDate);
            return;
        }
        
        // Format date with time component preserved (YYYY-MM-DDTHH:MM format for datetime-local)
        const year = newDate.getFullYear();
        const month = String(newDate.getMonth() + 1).padStart(2, '0');
        const day = String(newDate.getDate()).padStart(2, '0');
        const hours = String(newDate.getHours()).padStart(2, '0');
        const minutes = String(newDate.getMinutes()).padStart(2, '0');
        const dateTimeString = `${year}-${month}-${day}T${hours}:${minutes}`;
        
        const updatedTask = {
            ...task,
            CompleteByDate: task.WorkAssignment ? dateTimeString : task.CompleteByDate,
            WorkCompleteDate: task.ActualWork ? dateTimeString : task.WorkCompleteDate
        };
        
        console.log('Saving task with date:', dateTimeString, 'from UTC date:', newDate, updatedTask);
        await Api.saveTime(updatedTask);
        await loadTasks();
        draggedTask.value = null;
    } catch (error) {
        console.error('Failed to update task:', error);
        draggedTask.value = null;
    }
};

const handleDropTime = async (event: DragEvent, date: Date, hour: number) => {
    event.preventDefault();
    if (!draggedTask.value) return;
    
    try {
        const task = draggedTask.value;
        // Create date in UTC with specified hour
        const newDate = new Date(Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            hour, 0, 0, 0
        ));
        
        // Format date with time component (YYYY-MM-DDTHH:MM format for datetime-local)
        const year = newDate.getUTCFullYear();
        const month = String(newDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(newDate.getUTCDate()).padStart(2, '0');
        const hours = String(newDate.getUTCHours()).padStart(2, '0');
        const minutes = String(newDate.getUTCMinutes()).padStart(2, '0');
        const dateTimeString = `${year}-${month}-${day}T${hours}:${minutes}`;
        
        const updatedTask = {
            ...task,
            CompleteByDate: task.WorkAssignment ? dateTimeString : task.CompleteByDate,
            WorkCompleteDate: task.ActualWork ? dateTimeString : task.WorkCompleteDate
        };
        
        await Api.saveTime(updatedTask);
        await loadTasks();
        draggedTask.value = null;
    } catch (error) {
        console.error('Failed to update task:', error);
        draggedTask.value = null;
    }
};

// Watchers
watch(viewMode, () => {
    loadTasks();
});

watch(() => route.params.filter, () => {
    loadTasks();
}, { immediate: false });

// Lifecycle
onMounted(() => {
    loadTasks();
});
</script>

<style scoped>
.calendar-page {
    padding: 1rem;
    height: calc(100vh - 100px);
    display: flex;
    flex-direction: column;
}

.calendar-toolbar {
    flex-shrink: 0;
}

.calendar-container {
    flex: 1;
    overflow: hidden;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Month View */
.calendar-month {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: auto;
}

.calendar-header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    border-bottom: 2px solid #dee2e6;
    background: #f8f9fa;
    position: sticky;
    top: 0;
    z-index: 10;
}

.calendar-day-header {
    padding: 0.75rem;
    text-align: center;
    font-weight: 600;
    border-right: 1px solid #dee2e6;
}

.calendar-day-header:last-child {
    border-right: none;
}

.calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    grid-auto-rows: minmax(100px, auto);
    flex: 1;
}

.calendar-day {
    border: 1px solid #dee2e6;
    padding: 0.5rem;
    min-height: 100px;
    background: white;
    cursor: pointer;
    overflow: auto;
}

.calendar-day:hover {
    background: #f8f9fa;
}

.calendar-day.other-month {
    background: #f1f3f5;
    color: #adb5bd;
}

.calendar-day.today {
    background: #e7f5ff;
    border-color: #0d6efd;
}

.calendar-day.weekend {
    background: #f8f9fa;
}

.day-number {
    font-weight: 600;
    margin-bottom: 0.25rem;
    font-size: 0.875rem;
}

.day-tasks {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.task-item {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    cursor: move;
    border-left: 3px solid;
}

.task-item:hover {
    opacity: 0.8;
}

.task-assignment {
    background: #e8f4f8;
    border-left-color: #0dcaf0;
}

.task-work {
    background: #d1e7dd;
    border-left-color: #198754;
}

.task-selected {
    box-shadow: 0 0 0 2px #0d6efd;
}

.task-time {
    font-weight: 600;
    color: #495057;
}

.task-type-label {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #0d6efd;
    margin-bottom: 0.125rem;
}

.task-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.task-tech,
.task-client {
    font-size: 0.7rem;
    color: #6c757d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 0.125rem;
}

.task-tech i,
.task-client i {
    font-size: 0.65rem;
    opacity: 0.7;
}

/* Week View */
.calendar-week {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.week-header {
    display: grid;
    grid-template-columns: 80px repeat(7, 1fr);
    grid-column: 1 / -1;
    border-bottom: 2px solid #dee2e6;
    background: #f8f9fa;
    position: sticky;
    top: 0;
    z-index: 10;
}

.time-column-header {
    padding: 0.75rem;
    text-align: center;
    font-weight: 600;
    border-right: 1px solid #dee2e6;
}

.week-day-header {
    padding: 0.75rem;
    text-align: center;
    border-right: 1px solid #dee2e6;
}

.week-day-header.today {
    background: #e7f5ff;
    font-weight: 600;
}

.day-name {
    font-weight: 600;
}

.day-date {
    font-size: 0.875rem;
    color: #6c757d;
}

.week-grid {
    display: grid;
    grid-template-columns: 80px repeat(7, 1fr);
    grid-auto-rows: auto;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
}

/* Custom scrollbar styling to minimize width impact */
.week-grid::-webkit-scrollbar {
    width: 8px;
}

.week-grid::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.week-grid::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

.week-grid::-webkit-scrollbar-thumb:hover {
    background: #555;
}

.time-column {
    grid-column: 1;
    border-right: 2px solid #dee2e6;
    background: #f8f9fa;
    display: flex;
    flex-direction: column;
}

.time-slot {
    padding: 0.5rem;
    height: 60px;
    border-bottom: 1px solid #dee2e6;
    font-size: 0.75rem;
    text-align: right;
}

.week-day-column {
    border-right: 1px solid #dee2e6;
    min-width: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.week-day-column.today {
    background: #f8f9ff;
}

.week-time-slot {
    height: 60px;
    border-bottom: 1px solid #e9ecef;
    position: relative;
    padding: 0.25rem;
    min-width: 0;
    overflow: hidden;
}

.week-time-slot:hover {
    background: #f8f9fa;
}

.task-item-week {
    padding: 0.25rem;
    border-radius: 4px;
    font-size: 0.7rem;
    cursor: move;
    border-left: 3px solid;
    margin-bottom: 0.125rem;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
}

.task-title-week {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}

.task-tech-week,
.task-client-week {
    font-size: 0.65rem;
    color: #6c757d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 0.125rem;
    min-width: 0;
}

.task-tech-week i,
.task-client-week i {
    font-size: 0.6rem;
    opacity: 0.7;
}

.task-type-label-week {
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #0d6efd;
    margin-bottom: 0.125rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}

/* Day View */
.calendar-day-view {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.day-view-header {
    padding: 1rem;
    border-bottom: 2px solid #dee2e6;
    background: #f8f9fa;
    text-align: center;
}

.day-view-grid {
    display: grid;
    grid-template-columns: 80px 1fr;
    flex: 1;
    overflow: auto;
}

.day-view-column {
    border-left: 2px solid #dee2e6;
}

.day-time-slot {
    height: 80px;
    border-bottom: 1px solid #e9ecef;
    position: relative;
    padding: 0.5rem;
}

.day-time-slot:hover {
    background: #f8f9fa;
}

.task-item-day {
    padding: 0.5rem;
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: move;
    border-left: 4px solid;
    margin-bottom: 0.25rem;
}

.task-time-day {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.25rem;
}

.task-title-day {
    margin-bottom: 0.25rem;
}

.task-tech {
    font-size: 0.75rem;
    color: #6c757d;
}
</style>
